import { WSManager } from '@/hooks/ws-manager';

import {
  ListingItemDto,
  OperationItem,
  OperationsResponseDto,
  SearchMessage,
} from '@/entities/listing/typings/api-types';

export class AccommodationWSRepository {
  private _data: ListingItemDto[] | null = null;
  private _maxPrice: number | null = null;
  private _searchParams: SearchMessage | null = null;
  private subscribers: Set<() => void> = new Set<() => void>();
  private handlers: Record<
    string,
    (payload: OperationItem, prevData: ListingItemDto[]) => void
  > = {
    CLEAN: (payload, prevData) => {
      this.handleClean(payload, prevData);
    },
    INSERT: (payload, prevData) => {
      this.handleInsert(payload, prevData);
    },
    UPDATE: (payload, prevData) => {
      this.handleUpdate(payload, prevData);
    },
    MOVE: (payload, prevData) => {
      this.handleMove(payload, prevData);
    },
    DELETE: (payload, prevData) => {
      this.handleDelete(payload, prevData);
    },
  };

  constructor(private wsManager: WSManager<OperationsResponseDto, SearchMessage>) {
    this.wsManager = wsManager;
    this.setupListeners();
  }

  public connect(): void {
    this.wsManager.connect();
  }

  public close(): void {
    this.wsManager.close();
  }

  public setSearchParams = (searchParams: SearchMessage) => {
    this._searchParams = searchParams;
  };

  public subscribeToDataChanges(callback: () => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  private setupListeners() {
    this.wsManager.addListener((payload) => {
      this.handleMessage(payload);
    });
  }

  private notifySubscribers(): void {
    for (const callback of this.subscribers) callback();
  }

  private generateSearchKey = (params: SearchMessage) => {
    return `${params.filters.cityId}#${params.filters.arrivalDate}#${params.filters.departureDate}#${params.filters.adults}`;
  };

  private handleMessage = (data: OperationsResponseDto) => {
    if (this.searchParams == null) {
      return;
    }
    const currentSearchKey = this.generateSearchKey(this.searchParams);
    if (data.searchKey && data.searchKey !== currentSearchKey) {
      console.log(
        `SearchKey (${currentSearchKey}) does not match the currently selected parameters (${data.searchKey})`,
      );
      return;
    }

    if (!this.wsManager.sessionId && data.sessionId) {
      this.wsManager.setSessionId(data.sessionId);
    }

    if (data.operations.length === 0 && data.status.code === 404) {
      this._data = [];
      return;
    }

    if (data.operations.length === 0 && data.status.code === 200) {
      return;
    }

    if (data.operations.length === 1 && data.operations[0].type === 'CLEAN') {
      this._data = null;
      return;
    }

    const newData = this._data ? [...this._data] : [];

    const operations = data.operations;

    for (const operation of operations) {
      const handler = this.handlers[operation.type];
      handler(operation.item, newData);
    }

    this._data = newData;

    this.calculateMaxPrice();

    this.notifySubscribers();
  };

  private handleClean(_: OperationItem, newData: ListingItemDto[]) {
    newData.length = 0;
  }

  private handleInsert(item: OperationItem, newData: ListingItemDto[]) {
    if (item.position == null || !item.item) {
      return;
    }

    newData.splice(item.position, 0, item.item);
  }

  private handleUpdate(item: OperationItem, newData: ListingItemDto[]) {
    if (newData.length === 0 || item.position == null || !item.item) {
      return;
    }

    newData[item.position] = item.item;
  }

  private handleMove(item: OperationItem, newData: ListingItemDto[]) {
    if (newData.length === 0 || item.fromPosition == null || item.toPosition == null) {
      return;
    }

    const movableItem = newData[item.fromPosition];
    newData.splice(item.fromPosition, 1);
    newData.splice(item.toPosition, 0, movableItem);
  }

  private handleDelete(item: OperationItem, newData: ListingItemDto[]) {
    if (newData.length === 0 || item.position == null) {
      return;
    }

    newData.splice(item.position, 1);
  }

  public async subscribe(message: SearchMessage) {
    this.wsManager.sendMessage(message);
  }

  public async fetchMore(message: SearchMessage) {
    this.wsManager.sendMessage(message);
  }

  public get data() {
    return this._data;
  }

  public get maxPrice() {
    return this._maxPrice;
  }

  public get searchParams() {
    return this._searchParams;
  }

  public clearData() {
    this._data = null;
  }

  public isDifference(params: SearchMessage) {
    if (!this.searchParams) {
      return true;
    }

    return (
      params.filters.regionId !== this.searchParams.filters.regionId ||
      params.filters.cityId !== this.searchParams.filters.cityId ||
      params.filters.arrivalDate !== this.searchParams.filters.arrivalDate ||
      params.filters.departureDate !== this.searchParams.filters.departureDate ||
      params.filters.adults !== this.searchParams.filters.adults
    );
  }

  private calculateMaxPrice() {
    if (this._maxPrice || this._data === null || this._data.length === 0) {
      return;
    }
    this._maxPrice = Math.max(...this._data.map((item) => item.lowestPrice));
  }
}
